import {CpmCTaskApiModel} from '../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-task.api-model';
import {
    ECpmCTaskAttributes1ApiEnum,
    ECpmCTaskTypeApiEnum
} from '../../../gen/cpm-c-api-ts/src/enums/c-customer/cpmc-task.api-enums';
import {CpmCTaskUpdateApiSchema} from '../../../gen/cpm-c-api-ts/src/schemas/c-customer/cpmc-task.api-schemas';

/**
 * Interface for task option configuration
 */
interface TaskOption {
    tag: string;
    title: string;
    label: string;
    attribute: ECpmCTaskAttributes1ApiEnum;
    schemaField: keyof CpmCTaskUpdateApiSchema;
    applicableTypes: ECpmCTaskTypeApiEnum[];
}

/**
 * Interface for task option display
 */
export interface TaskOptionDisplay {
    tag: string;
    title: string;
    label: string;
    checked: boolean;
}

/**
 * Utility class for managing task options (attributes)
 */
export class TaskOptionsUtils {
    /**
     * Centralized configuration of all available task options
     */
    private static readonly OPTIONS_CONFIG: TaskOption[] = [
        {
            tag: 'group_auto_expand',
            title: 'Auto expand',
            label: 'Auto expand this group',
            attribute: ECpmCTaskAttributes1ApiEnum.GROUP_AUTO_EXPAND,
            schemaField: 'group_auto_expand',
            applicableTypes: [ECpmCTaskTypeApiEnum.GROUP]
        },
        {
            tag: 'is_milestone',
            title: 'Milestone',
            label: 'This task is a milestone',
            attribute: ECpmCTaskAttributes1ApiEnum.SIMPLE_IS_MILESTONE,
            schemaField: 'is_milestone',
            applicableTypes: [ECpmCTaskTypeApiEnum.SIMPLE]
        },
        {
            tag: 'smart_requires_manual_input',
            title: 'Requires manual input',
            label: 'This task requires manual input',
            attribute: ECpmCTaskAttributes1ApiEnum.SMART_REQUIRES_MANUAL_INPUT,
            schemaField: 'smart_requires_manual_input',
            applicableTypes: [ECpmCTaskTypeApiEnum.SMART]
        }
        // Future options can be easily added here
    ];

    /**
     * Get all applicable options for a given task
     * @param task The task to get options for
     * @returns Array of options with their current state
     */
    static getOptionsForTask(task: CpmCTaskApiModel): TaskOptionDisplay[] {
        return this.OPTIONS_CONFIG.filter((opt) => opt.applicableTypes.includes(task.task_type)).map((opt) => ({
            title: opt.title,
            label: opt.label,
            tag: opt.tag,
            checked: (task.attributes1 & opt.attribute) !== 0
        }));
    }

    /**
     * Build the update payload for a specific option
     * @param tag The option tag
     * @param checked The new checked state
     * @returns Update payload for the API, or empty object if tag is unknown
     */
    static buildUpdatePayload(tag: string, checked: boolean): CpmCTaskUpdateApiSchema {
        const option = this.OPTIONS_CONFIG.find((opt) => opt.tag === tag);
        if (!option) {
            return {} as CpmCTaskUpdateApiSchema;
        }

        return {[option.schemaField]: checked} as CpmCTaskUpdateApiSchema;
    }

    /**
     * Check if a specific option is enabled for a task
     * @param task The task to check
     * @param tag The option tag
     * @returns True if the option is enabled, false otherwise
     */
    static isOptionEnabled(task: CpmCTaskApiModel, tag: string): boolean {
        const option = this.OPTIONS_CONFIG.find((opt) => opt.tag === tag);
        if (!option) return false;

        return (task.attributes1 & option.attribute) !== 0;
    }

    /**
     * Get the attribute enum value for a specific option tag
     * @param tag The option tag
     * @returns The attribute enum value
     * @throws Error if the option tag is unknown
     */
    static getAttributeForTag(tag: string): ECpmCTaskAttributes1ApiEnum {
        const option = this.OPTIONS_CONFIG.find((opt) => opt.tag === tag);
        if (!option) {
            throw new Error(`Unknown option tag: ${tag}`);
        }

        return option.attribute;
    }
}
